import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/utils/apiValidation';
import { matchService } from '@/services/matchService';
import { QUERY_KEYS } from '@/constants';

export function useMatches(params = {}) {
  return useQuery({
    queryKey: [...QUERY_KEYS.MATCHES, params],
    queryFn: () => matchService.list(params),
  });
}

export function useMatch(id, options = {}) {
  return useQuery({
    queryKey: QUERY_KEYS.MATCH(id),
    queryFn: () => matchService.getById(id),
    enabled: Boolean(id),
    ...options,
  });
}

export function useCreateMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: matchService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MATCHES });
      toast.success('Match created successfully');
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to create match')),
  });
}

export function useUpdateMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => matchService.update(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MATCHES });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MATCH(id) });
      toast.success('Match updated successfully');
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to update match')),
  });
}

export function useDeleteMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: matchService.remove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MATCHES });
      queryClient.invalidateQueries({ queryKey: ['tournament-fixtures'] });
      toast.success('Match deleted');
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to delete match')),
  });
}

export function useStartMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (arg) =>
      typeof arg === 'string'
        ? matchService.start(arg)
        : matchService.start(arg.id, arg.toss),
    onSuccess: (_data, arg) => {
      const id = typeof arg === 'string' ? arg : arg.id;
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MATCHES });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MATCH(id) });
      queryClient.invalidateQueries({ queryKey: ['tournament-fixtures'] });
      toast.success('Match started');
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to start match')),
  });
}

export function useEndMatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: matchService.end,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MATCHES });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MATCH(id) });
      queryClient.invalidateQueries({ queryKey: ['tournament-fixtures'] });
      toast.success('Match ended');
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to end match')),
  });
}
